import { i18n, type Messages } from '@lingui/core';

import { detectLocale, type Locale } from './locales';

type CatalogModule = { messages: Messages };
type CatalogLoader = (locale: Locale) => Promise<CatalogModule>;

const baseCatalogs: CatalogLoader[] = [
  (locale) => import(`../../app/locales/${locale}.po`),
  (locale) => import(`../locales/${locale}.po`),
];

const moduleCatalogs = new Map<string, CatalogLoader>();
const loadedModules = new Set<string>();

/**
 * Registra o catálogo de um módulo. `eager`: carrega junto com a casca (o manifesto do módulo está no
 * menu, no ⌘K e no breadcrumb antes de a tela abrir); sem ele, só no `ensureModuleMessages`.
 */
export function registerModuleCatalog(moduleId: string, loader: CatalogLoader, options: { readonly eager?: boolean } = {}) {
  moduleCatalogs.set(moduleId, loader);
  if (options.eager && !loadedModules.has(moduleId)) {
    loadedModules.add(moduleId);
    // Registrado depois do boot (rota carregada sob demanda): carrega já no idioma ativo.
    if (i18n.locale) void loadInto(i18n.locale as Locale, [loader]);
  }
}

async function loadInto(locale: Locale, loaders: CatalogLoader[]) {
  const catalogs = await Promise.all(loaders.map((load) => load(locale)));
  for (const { messages } of catalogs) i18n.load(locale, messages);
}

export async function activateLocale(locale: Locale) {
  const loaders = [...baseCatalogs, ...[...loadedModules].flatMap((id) => moduleCatalogs.get(id) ?? [])];
  await loadInto(locale, loaders);
  i18n.activate(locale);
  document.documentElement.lang = locale;
}

export async function ensureModuleMessages(moduleId: string) {
  if (loadedModules.has(moduleId)) return;
  const loader = moduleCatalogs.get(moduleId);
  if (!loader) return;
  loadedModules.add(moduleId);
  await loadInto(i18n.locale as Locale, [loader]);
}

export function initialLocale(): Locale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem('uyzy.locale');
  } catch {
    stored = null;
  }
  return detectLocale(stored, navigator.languages);
}

export { i18n };
